import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { trackEvent } from "@/components/site/AnalyticsLoader";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_0455e07c-95ba-4da1-86cd-b6b640dcd3e8/artifacts/c1n4bm9e_Haceinda%20Carey%20Cover.png";

export default function Header() {
  const { lang, toggleLang, t } = useLanguage();
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "";
  const hashTarget = (id) => (isHome ? `#${id}` : `/#${id}`);
  const bookHref = hashTarget("activities");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: hashTarget("activities"), label: t.nav.activities, id: "activities" },
    { href: hashTarget("about"), label: t.nav.about, id: "about" },
    { href: hashTarget("reviews"), label: t.nav.reviews, id: "reviews" },
    { href: "/blog", label: t.nav.blog, id: "blog", route: true },
    { href: "/partners", label: t.nav.partners, id: "partners", route: true },
    { href: "/contact", label: t.nav.contact, id: "contact", route: true },
  ];

  // When the header is over a light page (e.g., /kids-farm hero is light), always use scrolled style.
  const forceLight = !isHome;
  const isLight = scrolled || forceLight;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        isLight
          ? "border-b border-[#E5DACC]/60 bg-[#FAF7F2]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-3 md:px-10 md:py-4">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="header-logo-link"
        >
          <img
            src={LOGO_URL}
            alt="Hacienda Carey"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-black/5"
          />
          <span
            className={`hidden text-lg leading-tight sm:flex sm:flex-col`}
            style={{ fontFamily: "Fraunces, serif" }}
          >
            <span
              className={`font-semibold ${
                isLight ? "text-[#0A0A0A]" : "text-white"
              }`}
            >
              Hacienda Carey
            </span>
            <span
              className={`text-[10px] uppercase tracking-[0.24em] ${
                isLight ? "text-[#FF5533]" : "text-[#FFB800]"
              }`}
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Puerto Rico
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav
          className="hidden items-center gap-8 lg:flex"
          aria-label="Primary navigation"
        >
          {links.map((l) => {
            const cls = `text-sm font-medium tracking-wide transition-colors ${
              isLight
                ? "text-[#0A0A0A]/80 hover:text-[#FF5533]"
                : "text-white/85 hover:text-white"
            }`;
            return l.route ? (
              <Link
                key={l.id}
                to={l.href}
                data-testid={`nav-link-${l.id}`}
                className={cls}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-link-${l.id}`}
                className={cls}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Language toggle */}
          <button
            type="button"
            onClick={() => {
              trackEvent("language_select", {
                language: lang === "en" ? "es" : "en",
                source: "header",
              });
              toggleLang();
            }}
            data-testid="header-language-toggle"
            aria-label="Toggle language"
            className={`group relative inline-flex h-10 items-center overflow-hidden rounded-full border px-1.5 text-xs font-semibold transition-all ${
              isLight
                ? "border-[#0A0A0A]/15 bg-white text-[#0A0A0A]"
                : "border-white/30 bg-white/10 text-white backdrop-blur-md"
            }`}
          >
            <span
              className={`flex h-7 w-9 items-center justify-center rounded-full transition-all ${
                lang === "en"
                  ? "bg-[#FF5533] text-white shadow"
                  : "text-current opacity-70"
              }`}
            >
              EN
            </span>
            <span
              className={`flex h-7 w-9 items-center justify-center rounded-full transition-all ${
                lang === "es"
                  ? "bg-[#FF5533] text-white shadow"
                  : "text-current opacity-70"
              }`}
            >
              ES
            </span>
          </button>

          {/* Book Now CTA */}
          <a
            href={bookHref}
            data-testid="header-book-button"
            className="hidden items-center gap-2 rounded-full bg-[#FF5533] px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-[#FF5533]/25 transition-all hover:-translate-y-0.5 hover:bg-[#E64A2B] hover:shadow-lg hover:shadow-[#FF5533]/35 md:inline-flex"
          >
            {t.nav.book}
            <span aria-hidden="true">→</span>
          </a>

          {/* Mobile menu trigger */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-full border lg:hidden ${
              isLight
                ? "border-[#0A0A0A]/15 text-[#0A0A0A]"
                : "border-white/30 bg-white/10 text-white backdrop-blur-md"
            }`}
            aria-label="Open menu"
            data-testid="header-mobile-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="border-t border-[#E5DACC]/60 bg-[#FAF7F2] lg:hidden"
          data-testid="header-mobile-menu"
        >
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4">
            {links.map((l) => {
              const cls = "rounded-xl px-3 py-3 text-base font-medium text-[#0A0A0A]/85 transition-colors hover:bg-[#FF5533]/5 hover:text-[#FF5533]";
              return l.route ? (
                <Link
                  key={l.id}
                  to={l.href}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className={cls}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className={cls}
                >
                  {l.label}
                </a>
              );
            })}
            <a
              href={bookHref}
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center justify-center rounded-full bg-[#FF5533] px-5 py-3 text-sm font-semibold text-white"
              data-testid="mobile-book-button"
            >
              {t.nav.book} →
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
